import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Search, ShieldCheck } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiFetch } from "@/lib/api";
import type { AuditRow } from "@/lib/master-data";
import { formatDateTime } from "@/lib/utils";

const CATEGORIES = [
  "identity",
  "asset",
  "accessory",
  "consumable",
  "license",
  "request",
  "label",
  "master_data",
  "personnel",
  "system",
];

export function AuditPage() {
  const [action, setAction] = useState("");
  const [category, setCategory] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AuditRow | null>(null);
  const [verifyResult, setVerifyResult] = useState<{
    valid: boolean;
    checked: number;
    brokenSeq?: number;
    reason?: string;
    truncated?: boolean;
  } | null>(null);
  const [verifying, setVerifying] = useState(false);
  const query = useQuery({
    queryKey: ["audit", action, category, from, to, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: "30" });
      if (action.trim()) params.set("action", action.trim());
      if (category) params.set("category", category);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      return apiFetch<{ items: AuditRow[]; total: number; page: number; pageSize: number }>(
        `/api/v1/audit?${params.toString()}`,
      );
    },
  });
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const pageSize = query.data?.pageSize ?? 30;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  async function verifyAll() {
    setVerifying(true);
    let checked = 0;
    let afterSeq = 0;
    let prevHash: string | undefined;
    try {
      for (let pageNo = 0; pageNo < 20; pageNo += 1) {
        const params = new URLSearchParams({ limit: "200" });
        if (afterSeq > 0 && prevHash) {
          params.set("afterSeq", String(afterSeq));
          params.set("prevHash", prevHash);
        }
        const result = await apiFetch<{
          valid: boolean;
          checked: number;
          brokenSeq?: number;
          reason?: string;
          truncated?: boolean;
          nextAfterSeq: number;
          nextPrevHash: string;
        }>(`/api/v1/audit/verify?${params.toString()}`);
        checked += result.checked;
        if (!result.valid) {
          setVerifyResult({ ...result, checked });
          return;
        }
        if (!result.truncated) {
          setVerifyResult({ valid: true, checked, truncated: false });
          return;
        }
        afterSeq = result.nextAfterSeq;
        prevHash = result.nextPrevHash;
      }
      setVerifyResult({ valid: true, checked, truncated: true });
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="审计日志"
        description="追加式审计链；记录操作人、被代批人、对象、前后值、请求 ID 与哈希，不提供修改或删除入口。"
        actions={
          <>
            <Button
              variant="outline"
              disabled={verifying}
              onClick={async () => {
                setVerifying(true);
                try {
                  const result = await apiFetch<{
                    valid: boolean;
                    checked: number;
                    brokenSeq?: number;
                    reason?: string;
                    truncated?: boolean;
                  }>("/api/v1/audit/verify?limit=200");
                  setVerifyResult(result);
                } finally {
                  setVerifying(false);
                }
              }}
            >
              <ShieldCheck className="h-4 w-4" />
              校验审计链
            </Button>
            <Button variant="outline" onClick={verifyAll} disabled={verifying}>
              <ShieldCheck className="h-4 w-4" />
              校验全部
            </Button>
            <Button variant="outline" onClick={() => query.refetch()} disabled={query.isFetching}>
              <RefreshCw className="h-4 w-4" />
              刷新
            </Button>
          </>
        }
      />

      {verifyResult ? (
        <Alert
          variant={verifyResult.valid ? "success" : "danger"}
          title={verifyResult.valid ? "审计链校验通过" : "审计链校验失败"}
        >
          {verifyResult.valid
            ? `已校验 ${verifyResult.checked} 条${verifyResult.truncated ? "（仅前 200 条）" : ""}，哈希链连续。`
            : `在第 ${verifyResult.brokenSeq} 条中断：${verifyResult.reason ?? "未知原因"}`}
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>筛选</CardTitle>
          <CardDescription>
            按动作、分类与日期范围检索；点击行查看完整前后值与哈希。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 md:grid-cols-5">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                value={action}
                onChange={(event) => {
                  setAction(event.target.value);
                  setPage(1);
                }}
                placeholder="动作，例如 ASSET_INBOUND"
              />
            </div>
            <select
              value={category}
              onChange={(event) => {
                setCategory(event.target.value);
                setPage(1);
              }}
              className="h-10 rounded-md border border-input bg-card px-3 text-sm"
            >
              <option value="">全部分类</option>
              {CATEGORIES.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <Input
              type="date"
              value={from}
              onChange={(event) => {
                setFrom(event.target.value);
                setPage(1);
              }}
            />
            <Input
              type="date"
              value={to}
              onChange={(event) => {
                setTo(event.target.value);
                setPage(1);
              }}
            />
            <Button
              variant="secondary"
              onClick={() => {
                setAction("");
                setCategory("");
                setFrom("");
                setTo("");
                setPage(1);
              }}
            >
              重置
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>审计事件（{total}）</CardTitle>
          <CardDescription>
            第 {page} / {totalPages} 页
          </CardDescription>
        </CardHeader>
        <CardContent>
          {query.isLoading ? (
            <div className="space-y-2">
              {["a1", "a2", "a3", "a4", "a5"].map((key) => (
                <Skeleton key={key} className="h-11 w-full" />
              ))}
            </div>
          ) : items.length === 0 ? (
            <EmptyState title="没有匹配的审计事件" description="调整筛选条件后重试。" />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>时间</TableHead>
                  <TableHead>分类</TableHead>
                  <TableHead>动作</TableHead>
                  <TableHead>对象</TableHead>
                  <TableHead>操作人</TableHead>
                  <TableHead>结果</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => setSelected(row)}
                  >
                    <TableCell className="text-xs">{formatDateTime(row.occurredAt)}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell className="font-mono text-xs">{row.action}</TableCell>
                    <TableCell className="text-xs">
                      {row.objectLabel ?? row.objectId ?? "—"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {row.actorUserId?.slice(0, 8) ?? "system"}…
                    </TableCell>
                    <TableCell>
                      <Badge variant={row.result === "success" ? "success" : "danger"}>
                        {row.result}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="mt-4 flex items-center justify-end gap-2">
            <Button
              variant="outline"
              disabled={page <= 1}
              onClick={() => setPage((current) => current - 1)}
            >
              上一页
            </Button>
            <Button
              variant="outline"
              disabled={page >= totalPages}
              onClick={() => setPage((current) => current + 1)}
            >
              下一页
            </Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={Boolean(selected)} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{selected?.action}</DialogTitle>
            <DialogDescription>
              {selected ? formatDateTime(selected.occurredAt) : ""} · {selected?.category}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-auto">
            <pre className="rounded-lg bg-muted p-3 text-xs">
              {JSON.stringify(
                {
                  actorUserId: selected?.actorUserId,
                  objectType: selected?.objectType,
                  objectId: selected?.objectId,
                  objectLabel: selected?.objectLabel,
                  requestId: selected?.requestId,
                  before: selected?.before,
                  after: selected?.after,
                  diff: selected?.diff,
                  hash: selected?.hash,
                },
                null,
                2,
              )}
            </pre>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
